import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Provider } from 'react-aria-components';

import { colors } from '@infinex/ui-constants';
import { IconContext } from '@infinex/ui-icons/context';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, componentVars, useStyleProps } from '@infinex/ui-styles';
import { isReactText } from '@infinex/ui-utils/react';

import { Text, TextContext } from '../content';

const BadgeCssVars = componentVars({ color: '--Badge-color' });

type ColorDiscriminator =
  | {
      /**
       * Used for decorative badges, that are not semantically meaningful.
       */
      color: string;
    }
  | {
      /**
       * Used for badges that are semantically meaningful.
       */
      tone: keyof typeof BADGE_TONE;
    };

export type BadgeProps = {
  /** The contents of the badge. */
  children: ReactNode;
  /**
   * The prominence of the badge. Use "default" for most cases, reserve "high"
   * prominence for cases where the badge must attract significant attention.
   *
   * @default default
   */
  prominence?: 'default' | 'high';
} & ColorDiscriminator &
  DOMProps &
  BaseStyleProps &
  UnsafeStyleProps;

/**
 * A badge is a decorative indicator used to either call attention to an item or
 * for communicating non-actionable, supplemental information.
 *
 * [Taurus docs: `Badge`](https://ui-sb.app.infinex.dev/?path=/docs/components-badge--docs)
 */
export const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  function Badge(props, ref) {
    const { className, style } = useStyleProps(props);
    const { children, prominence = 'default', ...otherProps } = props;

    return (
      <div
        {...filterDOMProps(otherProps)}
        ref={ref}
        className={cn(
          'inline-flex h-5 items-center rounded-full px-1 text-xs font-semibold whitespace-nowrap transition-colors select-none',
          {
            'font-numeric': !isNaN(Number(children)),
            [BadgeCssVars.tw('bg-(--Badge-color)/10', 'text-(--Badge-color)')]:
              prominence === 'default',
            [BadgeCssVars.tw('bg-(--Badge-color) text-black/80')]:
              prominence === 'high',
          },
          className
        )}
        style={{ ...BadgeCssVars.apply({ color: getColor(props) }), ...style }}
      >
        <Provider
          values={[
            [IconContext, { size: '2xsmall' }],
            [TextContext, { className: 'ms-0.5 me-1 only:mx-1' }],
          ]}
        >
          {isReactText(children) ? <Text>{children}</Text> : children}
        </Provider>
      </div>
    );
  }
);

// Utils
// -----------------------------------------------------------------------------

function getColor(props: BadgeProps) {
  if ('tone' in props) {
    return BADGE_TONE[props.tone];
  }

  return props.color;
}

export const BADGE_TONE = {
  neutral: colors.text.secondary,
  brand: colors.text.brand,
  highlight: colors.text.highlight,
  info: colors.text.info,
  positive: colors.text.positive,
  caution: colors.text.caution,
  critical: colors.text.critical,
};
